/**
 * Session Context - Authentication State Management
 *
 * This context provides centralized authentication state management for the Aegis application.
 * It handles user sessions, authentication status, and provides methods for session operations.
 * The context is used by the SessionProvider component and consumed via the useSession hook.
 *
 * @context
 * @description
 * ## Authentication State
 *
 * The context manages the following authentication states:
 * - **user**: Current authenticated user object from Supabase
 * - **isLoading**: Loading state during authentication operations
 * - **csrfToken**: CSRF protection token for secure operations
 *
 * ## Available Methods
 *
 * - **signOut()**: Signs out the current user and clears session
 * - **refreshSession()**: Refreshes the current session token
 *
 * ## Usage Pattern
 *
 * ```tsx
 * import { useSession } from '@/hooks/useSession';
 *
 * function MyComponent() {
 *   const { user, isLoading, signOut } = useSession();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (!user) return <div>Please log in</div>;
 *
 *   return <div>Welcome {user.email}</div>;
 * }
 * ```
 *
 * @see SessionProvider
 * @see useSession
 *
 * @since 1.0.0
 * @author Aegis Development Team
 */

import { createContext } from "react";
import type { User } from "@supabase/supabase-js";
import type { AdminZone } from "@/lib/rbac";

/**
 * Session context type definition
 */
export type SessionContextType = {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  csrfToken: string | null;
  demoModeEnabled: boolean;
  demoModeLoading: boolean;
  /** The zone the admin has selected to operate in (admin-only feature) */
  adminSelectedZone: AdminZone;
  /** Set the admin's selected zone and persist to localStorage */
  setAdminSelectedZone: (zone: AdminZone) => void;
  /** Whether the current user is a platform admin */
  isAdmin: boolean;
};

/**
 * Session context for authentication state
 * Used by SessionProvider and useSession hook
 */
export const SessionContext = createContext<SessionContextType | undefined>(undefined);
